import Backbone from 'backbone';
import $ from 'jquery';
import BaseApplication from 'modules/base/Application';
import environment from 'modules/utils/environment';
import AppConfig, { addConfig } from 'modules/config';
import TrackerProxy from './components/proxy/trackerProxy';
import AppChannel from 'modules/channels/App';
import ContextService from './entities/context/context.service';
import Impersonate from 'modules/impersonate';
import * as OAuth from 'modules/oauth';
import URI from 'URIjs';
import { getPersistedCampaign, forgetCampaign } from 'modules/campaignTracker';
import { updateUser } from 'api/users';

Backbone.$ = $;
import * as localStorage from 'modules/localstorage';
import { handleWebPushAtStartup } from 'modules/webpush';
import { dispatchLoadCurrentUser } from 'modules/utils/windowReduxStore.js';
import { isEmailValid } from 'modules/utils/string.js';

// Error reporting should be loaded after all external dependencies, but before all lrqdo code.
import { setBundle, enableReporting, setUser } from 'modules/error-reporting';
setBundle('main-app');
enableReporting();

const App = new BaseApplication();

function startRouter(locale) {
    if (!Backbone.history.started) {
        Backbone.history.start({
            pushState: true,
            root: `/${locale}/`,
        });
    }
}

App.once('start', function(options) {
    AppChannel.vent.trigger('before:start');

    const uri = new URI();
    const parseAsMap = true;
    const query = uri.search(parseAsMap);
    if (query.isAndroidTWA === localStorage.LOCAL_STORAGE_TRUE) {
        localStorage.setItem(localStorage.IS_ANDROID_TWA, localStorage.LOCAL_STORAGE_TRUE);
    }

    const context = ContextService.getCurrentContext();

    context.on('change', function() {
        AppChannel.vent.trigger('context:change', context);
    });

    const trackerProxy = new TrackerProxy(AppChannel.vent);
    trackerProxy.start();

    AppChannel.vent.on('login', id => {
        setUser({ id });
        handleWebPushAtStartup();
    });
    AppChannel.vent.on('logout', () => setUser(null));

    // Maintenance
    AppChannel.vent.on('http:error', status => {
        if (status === 503) {
            AppChannel.commands.execute('navigate', '/maintenance', true);
        }
    });

    // General click handler
    $(document).on(
        'click',
        'a:not([role=button], [data-bypass], .bypass, [target], .ck-link_selected, .ck-link-actions__preview)',
        function(evt) {
            const $this = $(this);
            const path = $this.attr('href');
            const protocol = `${this.protocol}//`;

            // Do not apply when trying to open in a new tab
            if (evt.metaKey || evt.ctrlKey) {
                return;
            }

            if ($this.data('behavior') === 'back') {
                evt.preventDefault();
                Backbone.history.history.back();
                return;
            }

            // Do not continue if no path
            if (!path) {
                return;
            }

            // Do not intercept absolute links
            if (path.slice(protocol.length) === protocol) {
                return;
            }

            // Remove the locale from the url if present
            const href = path.replace(Backbone.history.root, '');
            evt.preventDefault();
            AppChannel.commands.execute('navigate', href, true);
        }
    );

    $(document)
        .ajaxSuccess(function() {})
        .ajaxError(function(event, response) {
            if (response.status === 401) {
                AppChannel.commands.execute('logout', {
                    redirectUrl: Backbone.history.getFragment(),
                });
            } else if (response.status === 503) {
                AppChannel.vent.trigger('http:error', response.status);
            } else if (response.status >= 500) {
                AppChannel.vent.trigger('http:error', response.status);
            } else if (response.status === 0 && !response.silentAbort) {
                AppChannel.vent.trigger('http:error', response.status);
            }
        });

    addConfig(options);

    // if logout, just start and logout => allow some cleanup in case of bugs and stuff
    const logout = /\/logout$/.exec(location);
    if (logout && logout.length === 1) {
        return startRouter(options.locale);
    }

    const sessionPromise = OAuth.grant('refresh_token');

    if (sessionPromise) {
        const splitQuestionMark = location.href.split('?');

        // if we try to impersonate, change cookie before router start & user load
        const match = /\/impersonate\/(.+)$/.exec(splitQuestionMark[0]);
        const maybeRedirectUrl = new URLSearchParams(window.location.search).get('redirectUrl');

        if (match && isEmailValid(match[1])) {
            Impersonate.set(match[1]);
        } else if (match) {
            // if was a match but invalid email, router will pick up the slack
            // and re route to classic login
            Impersonate.clear();
        } else {
            // Was already impersonating something ?
            const impersonate = Impersonate.getImpersonated();
            if (impersonate) {
                // then actually set up the logic
                Impersonate.set(impersonate);
            }
        }

        // OAuth
        sessionPromise
            .done(() => {
                // Load user (impersonate?)
                const currentPathWithoutLocale = new URI().segment(0, '').path();
                if (maybeRedirectUrl) {
                    startRouter(options.locale);
                    window.location = Backbone.history.root + maybeRedirectUrl;
                    return;
                }

                dispatchLoadCurrentUser({ redirectUrl: currentPathWithoutLocale })
                    .then(user => {
                        const lastMarketingCampaign = getPersistedCampaign();
                        if (lastMarketingCampaign) {
                            // @NOTE: fire & forget
                            updateUser(user.id, { lastMarketingCampaign });
                            forgetCampaign();
                        }
                        AppChannel.vent.trigger('login', user.id);
                        startRouter(options.locale);
                    })
                    .catch(() => {
                        startRouter(options.locale);
                        AppChannel.commands.execute('logout', { reload: false });
                    });
            })
            .fail(() => {
                startRouter(options.locale);
                AppChannel.commands.execute('logout', { reload: false });
            });
    } else {
        startRouter(options.locale);
    }
});

$(document).on(
    'click',
    '.dropdown-menu a, .dropdown-menu input, .dropdown-menu label, .dropdown-menu button',
    function(e) {
        e.stopPropagation();
    }
);

AppChannel.commands.setHandler('register:instance', function(instance, id) {
    if (process.env.NODE_ENV === 'development') {
        App.register(instance, id);
    }
});

AppChannel.commands.setHandler('unregister:instance', function(instance, id) {
    if (process.env.NODE_ENV === 'development') {
        App.unregister(instance, id);
    }
});

AppChannel.commands.setHandler('change:locale', function(locale, url) {
    if (___DEV___) {
        return;
    }
    Backbone.history.root = Backbone.history.root || `/${AppConfig.locale}/`;
    let newUrl;
    if (!url) {
        newUrl = locale + Backbone.history.getFragment();
    } else {
        newUrl = locale + url;
    }
    Backbone.history.location.assign(newUrl);
});

AppChannel.commands.setHandler('navigate', function(url, options = {}) {
    return Backbone.history.navigate(url, options);
});

if (___DEV___ || environment !== 'production') {
    require.ensure(
        [],
        require => {
            require('./watermark').default(environment);
        },
        'watermarkChunk'
    );
}

export default App;
